import {defineRule} from 'vee-validate';
import * as rules from '@vee-validate/rules';
import moment from 'moment';

const nameRegex = /^[a-zA-ZÀ-ž\s.,]+$/;
const dateRegex = /^(0[1-9]|[12][0-9]|3[01])[. /](0[1-9]|1[012])[. /](19|20)\d\d$/;
const phoneNumberRegex = /^\+\d{12}$/;

const usedRules = ['required', 'min', 'max', 'email', 'alpha_spaces', 'confirmed', 'numeric', 'size', 'between', 'length'];

function ageBetween(value: string, args: any[]): boolean {
    const date = moment(value, 'DD.MM.YYYY');
    const age = moment().diff(date, 'years');
    return age >= args[0] && age <= args[1];
}

export function defineRules() {
    usedRules.forEach(rule => defineRule(rule, rules[rule]));

    defineRule('date', (value: string) => dateRegex.test(value));
    defineRule('tel', (value: string) => phoneNumberRegex.test(value));
    defineRule('age_between', ageBetween);
    defineRule('name', (value: string) => nameRegex.test(value));
    const fieldDateMap = {
        schoolRoundsStart: '',
        schoolRoundsEnd: '',
        regionRoundsStart: '',
        regionRoundsEnd: '',
        nationwideRoundsStart: '',
        nationwideRoundsEnd: ''
    };
    defineRule('dateAfter', (value: string, [targetField]: string, ctx) => {
        if (!value) return true;

        const fieldName = ctx?.field;
        const [day, month, year] = value.split('.');

        const valueDate = new Date(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10)
        );

        fieldDateMap[fieldName] = valueDate.toISOString();

        if (!targetField) return true;
        const targetValue = fieldDateMap[targetField];

        if (!targetValue) {
            return true;
        }
        const targetDate = new Date(targetValue);

        if (valueDate < targetDate) {
            targetDate.setDate(targetDate.getDate() - 1);
            const formattedDate = new Intl.DateTimeFormat('en-GB').format(targetDate);
            return `Dátum musí byť po ${formattedDate}`;
        }

        return true;
    });
    defineRule('defined', (value: unknown) => {
        if (value === undefined) {
            return 'Toto pole je povinné';
        }
        return true;
    });
}

interface ValidationSchema {
    [key: string]: string
}

export const userInfoProfileSchema: ValidationSchema = {
    first_name: 'required|name|max:50',
    last_name: 'required|name|max:50',
    email: 'required|email'
};

export const passwordSchema: ValidationSchema = {
    password: 'required|min:8',
    password_confirmation: 'required|confirmed:@password'
};

export const garantProfileSchema: ValidationSchema = {
    'garant_profile.levels': 'required'
};

export const studentProfileSchema: ValidationSchema = {
    'student_profile.date_of_birth': 'required|date',
    'student_profile.phone_number': 'required|tel',
    'student_profile.school_id': 'required|numeric',
    'student_profile.grade': 'required|between:1,5',
    'student_profile.street': 'required',
    'student_profile.city': 'required',
    'student_profile.zipcode': 'required|length:5'
};

export const judgeProfileSchema: ValidationSchema = {
    'judge_profile.contest_rounds': '',
    'judge_profile.contest_categories': ''
};

export const vskProfileSchema: ValidationSchema = {
    'vsk_profile.region_id': 'required|numeric'
};

export function mergeSchema(...schemas: ValidationSchema[]): ValidationSchema {
    const mergedSchema: ValidationSchema = {};
    for (const schema of schemas) {
        Object.assign(mergedSchema, schema);
    }
    return mergedSchema;
}

export const garantUserSchema = mergeSchema(userInfoProfileSchema, garantProfileSchema);
export const vskUserSchema = mergeSchema(userInfoProfileSchema, vskProfileSchema);

export const contactPersonSchema: ValidationSchema = {
    'region_id': 'required|numeric',
    'user_id': 'required|numeric'
};
